import { Persistence } from "../../config/constants";
import { TickLayerName } from "../../config/tickLayers";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { getMaterialName } from "../../game/material-helpers";
import { getSellValue } from "../../game/material-values";
import {
  customerSpan,
  CUSTOMER_BROWSE_MINUTES,
  CUSTOMER_BUY_CHANCE,
  CUSTOMER_SPAWN_CHANCE_EMPTY,
  CUSTOMER_SPAWN_CHANCE_STOCKED,
  CUSTOMER_SPEED,
  MAX_CUSTOMERS,
  pickPurchase,
  roundToCents,
  roundToHundredth,
  saleReputationGain,
  standFrontage,
} from "../../game/stand";
import { shareOverMinutes } from "../../game/time";
import { CustomerEntity } from "../entities/CustomerEntity";
import { StandEntity } from "../entities/StandEntity";
import { Clock } from "../singletons/Clock";
import { Progression } from "../singletons/Progression";
import { Reputation } from "../singletons/Reputation";
import { ShopInfo } from "../singletons/ShopInfo";
import { Wallet } from "../singletons/Wallet";
import { TimeFlow } from "../TimeFlow";

/**
 * The street's slice of the engine tick. Every engine tick, for the shop
 * minutes TimeFlow brought: customers stroll the sidewalk, one may stop
 * at a stocked stand and buy, and finished walkers step off the ends of
 * the block. Runs on the "street" layer, after the clock — so the span
 * it serves ended at the clock's reading and began that many minutes
 * back, which is what it reads night from.
 *
 * A span longer than a minute is served in minute-long steps
 * (`MAX_STEP`): customers cross cells, and one long step could carry a
 * walker past the stand without ever browsing. A charged batch — a
 * drive's leg, the overnight — therefore lands exactly where the same
 * minutes served one per tick would; the live pace's slices are already
 * far shorter than a minute.
 *
 * The street rolls dice — who walks by, who buys — and all of them come
 * from `game.random`, so a seeded driver lands the same buyers every
 * run. The spawn chance is a per-minute rate spread over the step
 * (`shareOverMinutes`), so the odds per hour don't depend on how finely
 * the clock slices it.
 *
 * A sale settles instantly — money into the Wallet, reputation into its
 * ledger, the lifetime count into Progression — and announces itself as
 * a "payout" event, which the reward-flight view turns into the
 * cha-ching and the fly-in. The first-sale unlocks (the store) stay the
 * milestone pass's job, keyed off `salesCompleted`.
 *
 * The first sale ever is dealt, not rolled: until something has sold, a
 * stocked stand summons its customer immediately and the browse below
 * always buys. The buyer still walks the block in shop minutes, so the
 * system registers that pending sale as a TimeFlow spender — the clock
 * runs at working pace rather than the idle creep while it's owed, so
 * the walk-up passes in seconds and the sale pays off while the player
 * is still standing there. It yields to the wait key (a held wait may
 * spin past it) and to night, when the street is empty and the sale
 * waits for morning. A scripted beat, not an economy: from the second
 * sale on the street runs on the dice and spends nothing.
 *
 * Transient — never serialized; added per session by the bootstrap.
 */
export class StreetSystem extends BaseEntity implements Entity {
  id = "streetSystem";
  tickLayer: TickLayerName = "street";
  persistenceLevel: number = Persistence.Permanent;

  private unregisterSpender: (() => void) | null = null;

  @on("afterAdded")
  onAfterAdded() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    if (timeFlow) {
      this.unregisterSpender = timeFlow.registerSpender(
        () => this.firstSalePending(),
        { yieldsToWait: true },
      );
    }
  }

  @on("destroy")
  onDestroy() {
    this.unregisterSpender?.();
    this.unregisterSpender = null;
  }

  /**
   * Whether the shop's very first sale is pending: something is out on
   * the stand and nothing has ever sold.
   */
  firstSalePending(): boolean {
    const stand = this.game.entities.tryGetSingleton(StandEntity);
    const progression = this.game.entities.tryGetSingleton(Progression);
    return (
      stand !== undefined &&
      progression !== undefined &&
      progression.salesCompleted === 0 &&
      stand.pieces.length > 0
    );
  }

  @on("tick")
  onTick() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    const minutes = timeFlow?.gameDt ?? 0;
    if (minutes <= 0) {
      return;
    }
    // The clock counted this engine tick's minutes before the street
    // layer ran, so the span began that many back.
    const start = this.game.entities.getSingleton(Clock).minute - minutes;
    let elapsed = 0;
    while (minutes - elapsed > STEP_EPSILON) {
      const step = Math.min(MAX_STEP, minutes - elapsed);
      this.pass(start + elapsed, step);
      elapsed += step;
    }
  }

  /**
   * `minutes` of the street, beginning at shop minute `at`.
   */
  private pass(at: number, minutes: number): void {
    const game = this.game;
    const rng = game.random;
    const shopInfo = game.entities.getSingleton(ShopInfo).info;
    const clock = game.entities.getSingleton(Clock);
    const stand = game.entities.getSingleton(StandEntity);
    const progression = game.entities.getSingleton(Progression);
    // A destroyed entity leaves the list only at the end of the engine
    // tick, so a batch of steps still finds the walkers who stepped off
    // the block during it. They have left the street.
    const customers = [...game.entities.byConstructor(CustomerEntity)].filter(
      (customer) => !customer.isDestroyed,
    );

    const span = customerSpan(shopInfo);
    const frontage = standFrontage(shopInfo);

    // Foot traffic follows the stand: a stocked table draws people down
    // the street, a bare one sees the odd stroller. Nobody at night.
    const spawnChance = shareOverMinutes(
      stand.pieces.length > 0
        ? CUSTOMER_SPAWN_CHANCE_STOCKED
        : CUSTOMER_SPAWN_CHANCE_EMPTY,
      minutes,
    );
    const firstSaleDue =
      progression.salesCompleted === 0 && stand.pieces.length > 0;
    // The guaranteed customer is only summoned while nobody already on
    // the street could still make the sale — one at a time, no crowd.
    const buyerEnRoute = customers.some(
      (customer) =>
        customer.state === "browsing" ||
        (customer.state === "walking" &&
          (customer.walkDirection === 1
            ? customer.x < frontage.right
            : customer.x > frontage.left)),
    );
    const spawning =
      !clock.isNightAt(at) &&
      customers.length < MAX_CUSTOMERS &&
      ((firstSaleDue && !buyerEnRoute) || rng() < spawnChance);
    if (customers.length === 0 && !spawning) {
      return;
    }

    const payouts: { title: string; money: number; reputation: number }[] = [];

    for (const customer of customers) {
      if (customer.state === "browsing") {
        customer.browseMinutesLeft -= minutes;
        if (customer.browseMinutesLeft > STEP_EPSILON) {
          continue;
        }
        // Decision time. One purchase per browse at most; either way
        // they move along afterwards and don't stop again. The shop's
        // first sale skips the coin flip — see the header.
        const purchase =
          progression.salesCompleted === 0 || rng() < CUSTOMER_BUY_CHANCE
            ? pickPurchase(stand.pieces, rng)
            : null;
        if (purchase) {
          const value = roundToCents(getSellValue(purchase));
          const reputationGain = saleReputationGain(value);
          stand.pieces = stand.pieces.filter((item) => item !== purchase);
          const wallet = game.entities.getSingleton(Wallet);
          wallet.money = roundToCents(wallet.money + value);
          const reputation = game.entities.getSingleton(Reputation);
          reputation.reputation = roundToHundredth(
            reputation.reputation + reputationGain,
          );
          progression.salesCompleted += 1;
          game.dispatch("standChanged", {});
          game.dispatch("progressionChanged", {});
          payouts.push({
            title: getMaterialName(purchase),
            money: value,
            reputation: reputationGain,
          });
        }
        customer.state = "leaving";
        customer.browseMinutesLeft = 0;
        continue;
      }

      const x = customer.x + CUSTOMER_SPEED * customer.walkDirection * minutes;
      if (x < span.left || x > span.right) {
        customer.destroy(); // walked off the end of the block
        continue;
      }
      customer.x = x;
      if (
        customer.state === "walking" &&
        stand.pieces.length > 0 &&
        x >= frontage.left &&
        x <= frontage.right
      ) {
        customer.state = "browsing";
        customer.browseMinutesLeft = CUSTOMER_BROWSE_MINUTES;
      }
    }

    if (spawning) {
      const walkDirection: 1 | -1 = rng() < 0.5 ? 1 : -1;
      progression.customersSeen += 1;
      game.addEntity(
        new CustomerEntity({
          // Numbered off the lifetime count, so no two walkers ever share
          // an id however the clock is sliced.
          id: `customer-${progression.customersSeen}`,
          x: walkDirection === 1 ? span.left : span.right,
          walkDirection,
          state: "walking",
          browseMinutesLeft: 0,
        }),
      );
    }

    payouts.forEach((payout, index) => {
      game.dispatch("payout", {
        payout: {
          // Numbered off the lifetime sales count (already advanced past
          // this step's sales), so no two announcements share an id.
          id: `payout-sale-${progression.salesCompleted - payouts.length + index + 1}-${payout.title}`,
          kind: "sale",
          title: payout.title,
          money: payout.money,
          reputation: payout.reputation,
          xp: 0,
        },
      });
    });
  }
}

/**
 * The longest span one street pass serves, in shop minutes — see the
 * class header.
 */
const MAX_STEP = 1;

/** Below this, a leftover span (or browse) is float noise, not time. */
const STEP_EPSILON = 1e-9;
